import React from 'react';
import styled from 'styled-components';
import Tippy from '@tippyjs/react';

import t from '@zimpel/common/helpers/i18n';
import type { IconType } from '@zimpel/common/components/Icon';
import {
  EmailIcon, PhoneIcon, TimeIcon, VideoIcon,
} from '@zimpel/common/components/Icon';
import { AppLink, EmailLink } from '@zimpel/common/components/Links';
import { UnstyledList } from '@zimpel/common/components/Lists';

const StyledSupportInfo = styled(Tippy)`
  font-size: var(--font-size-small);
  font-family: var(--font-family-default);
  color: var(--color-text-default);
  background-color: white;
  border: var(--border-input);
  border-radius: 4px;
  min-width: 270px;
`;

const StyledLine = styled.li`
  padding-right: 8px;
  padding-left: 8px;
  
  &:not(:first-child) {
    border-top: var(--border-box);
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 28px;
`;

const ContentText = styled.span`
  padding-top: 5px;
  padding-bottom: 5px;
  text-align: right;
  flex-grow: 1;
`;

const getServiceTimeText = (theme: string): string => {
  switch (theme) {
    case 'renteria':
      return t('views.layout.header.service.time.renteria');
    default:
      return t('views.layout.header.service.time.zimpel');
  }
};

const WELCOME_VIDEO_LOCALES = ['de-DE-renteria', 'de-DE-zimpel', 'en-US-zimpel'];

type LineProps = {
  icon?: IconType
} & React.PropsWithChildren
const Line: React.FC<LineProps> = ({ icon, children }) => (
  <StyledLine>
    <Content>
      {icon && React.createElement(icon, { size: 'lg' })}
      <ContentText>
        {children}
      </ContentText>
    </Content>
  </StyledLine>
);

const SupportTrigger = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: var(--font-size-small);
`;

type Props = { theme: string; locale: string, visible?: boolean, loggedOut?: boolean };

const SupportInfo: React.FC<Props> = ({
  theme, locale, visible, loggedOut,
}) => (
  <StyledSupportInfo
    arrow={false}
    interactive
    offset={[18, -28]}
    placement="bottom-end"
    visible={visible}
    content={(
      <UnstyledList>
        <Line>
          <strong>{t('view.layout.header.service.header')}</strong>
        </Line>
        <Line icon={PhoneIcon}>
          {t('views.layout.header.service.phone_number')}
        </Line>
        <Line icon={EmailIcon}>
          <EmailLink>{t('views.layout.header.service.email')}</EmailLink>
        </Line>
        {!loggedOut && (
          <Line icon={EmailIcon}>
            <EmailLink>{t('views.layout.header.research.email')}</EmailLink>
          </Line>
        )}
        <Line icon={TimeIcon}>
          {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
          <span dangerouslySetInnerHTML={{ __html: getServiceTimeText(theme) }} />
        </Line>
        {!loggedOut && WELCOME_VIDEO_LOCALES.includes(locale) && (
          <Line icon={VideoIcon}>
            <AppLink to="/help" hasUnderline>{t('views.layout.header.service.video.text')}</AppLink>
          </Line>
        )}
      </UnstyledList>
    )}
  >
    <SupportTrigger>
      <PhoneIcon />
      <strong>{t('view.layout.header.service.header')}</strong>
    </SupportTrigger>
  </StyledSupportInfo>
);

export default SupportInfo;
